import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight, ChevronDown, Menu, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { LOGO_LOCKUP_DARK } from '../lib/brand';

export const Brand = () => <span className="wl-logo"><img src={LOGO_LOCKUP_DARK} alt="DutiLyne Pharmacy POS" className="wl-logo-mark" /></span>;

// Shared Escape-key + outside-click dismissal for any open disclosure panel
// (the mobile nav toggle and the "Explore" dropdown both use this).
const useDismiss = (open, close, containerRef) => {
  useEffect(() => {
    if (!open) return undefined;
    const escape = (event) => { if (event.key === 'Escape') close(); };
    const outside = (event) => { if (!containerRef.current?.contains(event.target)) close(); };
    document.addEventListener('keydown', escape);
    document.addEventListener('pointerdown', outside);
    return () => {
      document.removeEventListener('keydown', escape);
      document.removeEventListener('pointerdown', outside);
    };
  }, [open, close, containerRef]);
};

const EXPLORE_LINKS = [
  ['#showcase', 'All features', 'welcome-nav-showcase'],
  ['#clinical', 'The platform', 'welcome-nav-clinical'],
  ['#offline', 'Offline-first', 'welcome-nav-offline'],
  ['#ghana', 'Ghana pricing', 'welcome-nav-ghana'],
  ['#portal', 'Connected care', 'welcome-nav-portal'],
  ['#roi', 'ROI calculator', 'welcome-nav-roi'],
];

// Groups the five in-page anchor links behind one "Explore" disclosure so the
// top-level nav only shows the primary, conversion-focused links.
const ExploreMenu = ({ onNavigate }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const menuId = useId();
  const close = useCallback(() => setOpen(false), []);
  useDismiss(open, close, ref);

  return (
    <div className="wl-nav-dropdown" ref={ref} data-testid="welcome-nav-explore">
      <button type="button" className="wl-nav-dropdown-trigger" aria-expanded={open} aria-controls={menuId}
        onClick={() => setOpen((v) => !v)} data-testid="welcome-nav-explore-trigger">
        Explore <ChevronDown size={14} className={`wl-nav-chevron${open ? ' is-open' : ''}`} />
      </button>
      <div id={menuId} className={`wl-nav-dropdown-menu${open ? ' is-open' : ''}`} data-testid="welcome-nav-explore-menu">
        {EXPLORE_LINKS.map(([href, label, testId]) => (
          <a key={href} href={href} tabIndex={open ? 0 : -1} onClick={() => { close(); onNavigate?.(); }} data-testid={testId}>{label}</a>
        ))}
      </div>
    </div>
  );
};

export const WelcomeHeader = () => {
  const [open, setOpen] = useState(false);
  const toggle = useRef(null);
  const header = useRef(null);
  const closeMenu = useCallback(() => { setOpen(false); toggle.current?.focus(); }, []);
  useDismiss(open, closeMenu, header);

  return (
    <header className="wl-bar" ref={header}>
      <div className="wl-wrap wl-header-inner">
        <a href="#welcome-main" aria-label="DutiLyne home" data-testid="welcome-brand-link"><Brand /></a>
        <Button ref={toggle} variant="ghost" className="wl-menu-toggle" aria-expanded={open}
          aria-controls="welcome-navigation" aria-label={open ? 'Close navigation' : 'Open navigation'}
          onClick={() => setOpen(!open)} data-testid="welcome-menu-toggle">
          {open ? <X /> : <Menu />}
        </Button>
        <nav id="welcome-navigation" aria-label="Main navigation" className={`wl-nav ${open ? 'is-open' : ''}`} data-testid="welcome-navigation">
          <ExploreMenu onNavigate={() => setOpen(false)} />
          <Link to="/plans" data-testid="welcome-plans-link">Pricing</Link>
          <Link to="/portal/find" className="wl-nav-login" data-testid="welcome-header-find-pharmacy">Find pharmacy <ArrowUpRight size={14} /></Link>
          <Link to="/login" data-testid="welcome-header-staff-login">Staff sign-in <ArrowUpRight size={14} /></Link>
          <Link to="/signup/pharmacy" data-testid="welcome-header-signup">Start trial <ArrowUpRight size={14} /></Link>
          <Link to="/try-demo" data-testid="welcome-header-try-demo">Try the demo <ArrowUpRight size={14} /></Link>
          <a href="#demo" className="wl-button wl-button-primary" onClick={() => setOpen(false)} data-testid="welcome-hero-cta-top">Request a demo <ArrowRight size={15} /></a>
        </nav>
      </div>
    </header>
  );
};

export const WelcomeFooter = () => (
  <footer className="wl-footer">
    <div className="wl-wrap">
      <div className="wl-footer-top">
        <div><a href="#welcome-main" aria-label="DutiLyne back to top" data-testid="welcome-footer-brand"><Brand /></a>
          <p>Closer to your patients.<br />Connected to your pharmacy.</p></div>
        <div><h2>Explore</h2>
          <a href="#showcase" data-testid="welcome-footer-showcase">All features</a>
          <a href="#clinical" data-testid="welcome-footer-platform">The platform</a>
          <a href="#offline" data-testid="welcome-footer-offline">Offline-first</a>
          <a href="#ghana" data-testid="welcome-footer-ghana">Ghana pricing</a>
          <a href="#roi" data-testid="welcome-footer-roi">ROI calculator</a>
          <Link to="/plans" data-testid="welcome-footer-pricing">Plans & pricing</Link></div>
        <div><h2>Let’s connect</h2>
          <a href="mailto:info@dutilyne.com" data-testid="welcome-email">info@dutilyne.com <ArrowUpRight size={14} /></a>
          <a href="https://wa.me/233242160467" target="_blank" rel="noreferrer" data-testid="welcome-whatsapp">WhatsApp +233 24 216 0467 <ArrowUpRight size={14} /></a>
          <Link to="/portal/find" data-testid="welcome-find-pharmacy">Find a pharmacy <ArrowUpRight size={14} /></Link>
          <Link to="/login" data-testid="welcome-staff-login">Staff sign-in <ArrowUpRight size={14} /></Link>
          <Link to="/signup/pharmacy" data-testid="welcome-footer-signup">Create pharmacy <ArrowUpRight size={14} /></Link>
          <Link to="/try-demo" data-testid="welcome-footer-try-demo">Try the demo <ArrowUpRight size={14} /></Link>
          <Link to="/partners/psgh" data-testid="welcome-footer-partners">For PSGH & Pharmacy Council <ArrowUpRight size={14} /></Link></div>
      </div>
      <div className="wl-footer-bottom">
        <span>© {new Date().getFullYear()} DutiLyne. All rights reserved.</span>
        <span data-testid="welcome-imagery-disclosure">Care scenes are AI-generated illustrations, not actual customers or clinical evidence.</span>
      </div>
    </div>
  </footer>
);
